import { forwardRef, Ref } from 'react';
import styled from 'styled-components';
import { clamp } from './_clamp';
import { rem } from './_rem';
import { theme } from './badge_number.theme';

type Variant = 'interactive' | 'brandDark' | 'brandLight' | 'neutralLight' | 'neutralDark';
type Size = 'medium' | 'small';

interface withDecorative {
	/**
	 * A label to provide more context to assistive technologies. The format should follow: `"There is {{value}} new messages."`
	 * @example label='There is {{value}} new messages.'
	 */
	label?: never | string;

	/**
	 * Will hide the badge from assistive technologies. Use if the badge is only
	 * decorative and the information is provided somewhere else. E.g. if it's
	 * located inside a element with `aria-label`.
	 *
	 * @default false
	 */
	decorative: true;
}

interface withoutDecorative {
	label: string;
	decorative?: false;
}

type BadgeNumberProps = {
	/**
	 * The visual representation of the badge.
	 * @default interactive
	 */
	variant?: Variant;

	/**
	 * The visual size of the badge.
	 * @default medium
	 */
	size?: Size;

	/**
	 * The maximum number before the value is clamped. If `clampMax={9}` and
	 * `value={10}` it will render `9+`.
	 * @default 9
	 */
	clampMax?: number;

	/**
	 * Will display the badge even if the value is `0`.
	 * @default false
	 */
	displayZero?: boolean;

	/**
	 * The value to be displayed in the badge.
	 */
	value: number;
} & (withDecorative | withoutDecorative);

const elements = {
	ellipse: styled.div`
		--font: inherit;
		--background: transparent;
		--textColor: #000;

		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-variant-numeric: proportional-nums;
		box-sizing: border-box;
		user-select: none;
		background: var(--background);
		color: var(--textColor);
		font: var(--font);
		flex-shrink: 0;

		// Variants
		&[data-variant='interactive'] {
			--background: ${theme.getValue('interactiveBackground')};
			--textColor: ${theme.getValue('interactiveTextColor')};
		}
		&[data-variant='brandDark'] {
			--background: ${theme.getValue('brandDarkBackground')};
			--textColor: ${theme.getValue('brandDarkTextColor')};
		}
		&[data-variant='brandLight'] {
			--background: ${theme.getValue('brandLightBackground')};
			--textColor: ${theme.getValue('brandLightTextColor')};
		}
		&[data-variant='neutralDark'] {
			--background: ${theme.getValue('neutralDarkBackground')};
			--textColor: ${theme.getValue('neutralDarkTextColor')};
		}
		&[data-variant='neutralLight'] {
			--background: ${theme.getValue('neutralLightBackground')};
			--textColor: ${theme.getValue('neutralLightTextColor')};
		}

		// Sizes
		&[data-size='small'] {
			--font: ${theme.getValue('smallText')};

			min-width: ${rem(16)};
			border-radius: ${rem(16 / 2)};
			height: ${rem(16)};
			padding: 0 ${rem(4)};
		}
		&[data-size='medium'] {
			--font: ${theme.getValue('mediumText')};

			min-width: ${rem(20)};
			border-radius: ${rem(20 / 2)};
			height: ${rem(20)};
			// The 5.5 (rather than 6, which is in the design) is intentional,
			// it's nudging the text a bit so no 1px content shifting is occurring.
			padding: 0 ${rem(5.5)};
		}
	`,
	plus: styled.span`
		position: relative;
		// Optically centers the plus sign, without these it'll look "off".
		top: -${rem(1)};
		left: -${rem(1)};
		width: ${rem(5)};
	`,
};

function CellulaBadgeNumber(
	{ label, value, decorative = false, variant = 'interactive', size = 'medium', clampMax = 9, displayZero = false }: BadgeNumberProps,
	ref: Ref<HTMLDivElement>,
): JSX.Element | null {
	const displayAmount = clamp(value, [0, clampMax]);

	if (displayAmount === 0 && !displayZero) {
		return null;
	}

	return (
		<elements.ellipse
			aria-label={!decorative && label ? label.replace('{{value}}', value.toString()) : undefined}
			aria-hidden={decorative ? 'true' : undefined}
			data-variant={variant}
			data-size={size}
			ref={ref}
		>
			{displayAmount}
			{value > clampMax ? <span className='plus'>+</span> : null}
		</elements.ellipse>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component for displaying a badge with data of the `number` type.
 *
 * @deprecated Use the `Chip` component instead.
 *
 * @example
 * ```tsx
 * import { BadgeNumber } from '@cellula/react';
 *
 * interface Props {
 *   count: number;
 * }
 *
 * function App({ count }: Props): JSX.Element {
 *   return (
 *     <BadgeNumber value={count} size="small" clampMax={5} />
 *   );
 * }
 * ```
 */
const BadgeNumber = forwardRef(CellulaBadgeNumber);

export type { BadgeNumberProps };
export { BadgeNumber };
